import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Divider,
  Badge,
  Button,
  Box,
  Typography,
  CircularProgress
} from '@material-ui/core';
import { CloseIcon, QueryBuilder, TableComponent } from 'components';
import { Alert } from '@material-ui/lab';
import SaveMessage from 'views/UserMessaging/customModalContent/saveMessage';
import Skeleton from 'react-loading-skeleton';
import { ProductType } from 'constants.js';
import { useDispatch, useSelector } from 'react-redux';
import {
  getListsRecipientCount,
  getRecipientDetails,
  resetListRecipientCount
} from 'store/recipientQueryTemplate/actions';
import PopupModal from 'views/UserMessaging/customModalContent/popupModal';
import { getSchemas } from 'store/schema/actions';
import { resetValues } from 'store/helpers/tableAndToolbar/actions';
import { ccToPcConverter } from 'helpers/commonUtils';

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%'
  },
  cardHeader: {},
  radioLabel: {
    marginLeft: 0,
    alignItems: 'baseline'
  },
  radioGroup: {
    marginLeft: '-1rem'
  },
  cardActions: {
    flexDirection: 'row'
  },
  inputGrid: {
    maxWidth: '40rem'
  },
  templateButtonError: {
    paddingLeft: '19px',
    color: theme.palette.error.main
  },
  saveButton: {
    marginLeft: '14px !important'
  },
  counter: {
    marginRight: theme.spacing(1)
  },
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%'
  },
  title: {
    display: 'flex',
    flexDirection: 'row',
    width: '95%'
  },
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    margin: '10%'
  },
  noResultsDiv: {
    textAlign: 'center',
    padding: theme.spacing(2),
    fontSize: '16px'
  },
  countLabel: {
    display: 'flex',
    marginBottom: theme.spacing(2),
    marginLeft: 2
  },
  disableDiv: {
    opacity: 0.5,
    pointerEvents: 'none'
  },
  countData: {
    fontWeight: 400,
    marginLeft: theme.spacing(1)
  }
}));

const Design = props => {
  const {
    className,
    staticContext,
    template,
    back,
    showNav,
    isPopupDialog,
    ...rest
  } = props;
  const {
    goBack,
    navBackOnError,
    shouldShowError,
    attributes,
    thisTemplate,
    isQueryReadOnly,
    closeQuery,
    saveTemplate,
    isLoading,
    isTemplateSaving,
    updateCurrentErrorValidationSegment,
    handleChange,
    handleRecipientCount,
    handleRecipientDetails,
    isTemplateSaved
  } = template;

  const classes = useStyles();

  const requestId = useSelector(
    store => store.queryTemplate.listsCount.requestId
  );
  const listCount = useSelector(store => store.queryTemplate.listsCount.count);
  const isPolling = useSelector(
    store => store.queryTemplate.listsCount.isPolling
  );
  const pollingFailed = useSelector(
    store => store.queryTemplate.listsCount.pollingFailed
  );
  const endPolling = useSelector(
    store => store.queryTemplate.listsCount.endPolling
  );
  const schemaList = useSelector(store => store.schema.list);
  const recipientDetails = useSelector(
    store => store.queryTemplate.recipientDetails.details
  );
  const recipientDetailsCount = useSelector(
    store => store.queryTemplate.recipientDetails.count
  );
  const isLoadingDetails = useSelector(
    store => store.queryTemplate.recipientDetails.isLoading
  );
  const dispatch = useDispatch();

  const [showAlert, setShowAlert] = useState(false);
  const [showRecipientDetails, setShowRecipientDetails] = useState(false);
  const [isQueryModified, setIsQueryModified] = useState(false);
  const [eventIdentifierFields, setEventIdentifierFields] = useState([]);
  const [recipientDetailsColumns, setRecipientDetailsColumns] = useState([]);
  const [recipientDetailsRows, setRecipientDetailsRows] = useState([]);

  useEffect(() => {
    dispatch(resetListRecipientCount());
    dispatch(getSchemas({ showActive: true }));
  }, []);

  useEffect(() => {
    if (!eventIdentifierFields || eventIdentifierFields?.length === 0) {
      let currentSchema =
        schemaList.filter(
          schema =>
            schema.eventProductCategory === thisTemplate.productCategory &&
            schema.eventProductName === thisTemplate.productName &&
            schema.eventTypeCategory === thisTemplate.eventTypeCategory
        )[0] || {};
      setEventIdentifierFields(currentSchema.eventIdentifierFields || []);
    }
  }, [schemaList, thisTemplate]);

  useEffect(() => {
    updateCurrentErrorValidationSegment('design');
  }, [thisTemplate, updateCurrentErrorValidationSegment]);

  useEffect(() => {
    navBackOnError(back);
  }, [thisTemplate]);

  useEffect(() => {
    if (endPolling && requestId) {
      dispatch(getListsRecipientCount({ requestId }));
    }
  }, [endPolling, requestId]);

  useEffect(() => {
    if (pollingFailed) {
      setShowAlert(true);
    }
  }, [pollingFailed]);

  const handleClose = () => {
    setShowAlert(false);
  };

  const getButtonGroup = () => [{ title: 'Ok', action: () => handleClose() }];

  const createRecipientDetailsColumns = () => {
    let columns =
      eventIdentifierFields?.map(field => {
        return { id: field, label: ccToPcConverter(field) };
      }) || [];
    setRecipientDetailsColumns(columns);
  };

  const createRecipientDetailsRows = () => {
    let rows =
      recipientDetails?.map(details => {
        let data = details.configurableEvent ? details.configurableEvent : details;
        let eventValues = data.eventValues;
        return eventIdentifierFields.reduce((obj, key) => {
          return Object.assign(obj, {
            [key]: eventValues[key] || '-'
          });
        }, {});
      }) || [];
    setRecipientDetailsRows(rows);
  };

  useEffect(() => {
    createRecipientDetailsColumns();
    createRecipientDetailsRows();
  }, [recipientDetails]);

  const onClickRecipientDetails = () => {
    dispatch(resetValues());
    let isValid = handleRecipientDetails();
    if (isValid) {
      setShowRecipientDetails(isValid);
      setIsQueryModified(false);
    }
  };

  const onChangeQuery = event => {
    handleChange(event);
    setIsQueryModified(true);
  };

  const onSaveQuery = () => {
    saveTemplate();
    setShowRecipientDetails(false);
  };

  return (
    <div className={classes.root}>
      <CloseIcon
        style={{ float: 'right', paddingTop: 0 }}
        onClickClose={closeQuery}
      />
      <Card {...rest} className={clsx(classes.root, className)}>
        <form autoComplete="off">
          {!isPopupDialog && (
            <React.Fragment>
              <div>
                {isQueryReadOnly() && (
                  <Alert severity="info">
                    You do not have access to edit this query. Tenant Default
                    queries can be edited only with 'ALL' property.
                  </Alert>
                )}
              </div>
              <div className={classes.headerContainer}>
                <div className={classes.title}>
                  <CardHeader
                    title="Recipient Queries"
                    titleTypographyProps={{ variant: 'h1' }}
                  />
                </div>
              </div>
            </React.Fragment>
          )}
          <Divider />
          <CardContent>
            {isLoading && (
              <Skeleton
                style={{ marginLeft: '8px' }}
                variant="rectangular"
                width={300}
                height={20}
              />
            )}
            {!isLoading && thisTemplate.hasMovedToNextStep && (
              <QueryBuilder
                name="conditionExpression"
                template={{ thisTemplate, attributes, templateId: thisTemplate.conditionExpression?.id }}
                onChange={onChangeQuery}
                isReadOnly={isQueryReadOnly}
                isLoading={isLoading}
                query={thisTemplate.conditionExpression || {}}
                required="true"
              />
            )}
            {(shouldShowError(`conditionExpression.subConditionExpressions`) ||
              shouldShowError(`conditionExpression.valid`)) && (
              <Typography
                className={classes.templateButtonError}
                variant="caption"
                display="block">
                {shouldShowError(`conditionExpression.subConditionExpressions`)
                  ? '*Please add at least one rule.'
                  : '*Please enter a complete valid expression.'}
              </Typography>
            )}
          </CardContent>
          {(back || showNav || isPopupDialog) && (
            <>
              <Divider />
              <CardActions className={classes.cardActions}>
                {!isPopupDialog && (
                  <Button
                    color="secondary"
                    variant="contained"
                    disabled={isLoading || isTemplateSaving}
                    link={back}
                    onClick={goBack}>
                    Back
                  </Button>
                )}
                <Typography></Typography>
                <Box flexGrow={1}>&nbsp;</Box>
                {thisTemplate.productType === ProductType.List ? (
                  <Badge
                    style={{ marginRight: '16px' }}
                    max={99999}
                    color="secondary"
                    badgeContent={listCount || 0}
                    className={isQueryReadOnly() ? classes.counter : ''}>
                    <div>
                      <Button
                        color="primary"
                        variant="contained"
                        disabled={isTemplateSaving || isPolling}
                        onClick={() => handleRecipientCount()}>
                        Recipients Count
                      </Button>
                      {isPolling && (
                        <CircularProgress
                          size={24}
                          className={classes.buttonProgress}
                        />
                      )}
                    </div>
                  </Badge>
                ) : (
                  <Button
                    style={{ margin: '0px 16px' }}
                    color="primary"
                    variant="contained"
                    disabled={isLoadingDetails || isTemplateSaving || isLoading}
                    onClick={() => onClickRecipientDetails()}>
                    Recipients Details
                  </Button>
                )}
                {!isQueryReadOnly() && (
                  <Button
                    className={classes.saveButton}
                    color="primary"
                    variant="contained"
                    disabled={isLoading || isTemplateSaving}
                    onClick={onSaveQuery}>
                    Save Recipient Query
                  </Button>
                )}
              </CardActions>
            </>
          )}
        </form>
      </Card>

      {showRecipientDetails && (
        <Card {...rest} className={classes.root} style={{ marginTop: 40 }}>
          <form autoComplete="off">
            {
              <React.Fragment>
                {isQueryModified && (
                  <Alert severity="info">
                    To display recipient details for modified query, click on
                    'Recipients details' button.
                  </Alert>
                )}
                <div className={classes.headerContainer}>
                  <div
                    className={clsx(
                      classes.title,
                      isQueryModified ? classes.disableDiv : ''
                    )}>
                    <CardHeader
                      title="Recipient Details"
                      titleTypographyProps={{ variant: 'h1' }}
                    />
                  </div>
                  <CloseIcon
                    onClickClose={() => setShowRecipientDetails(false)}
                  />
                </div>
              </React.Fragment>
            }
            <Divider />
            <CardContent className={isQueryModified ? classes.disableDiv : ''}>
              {!isLoadingDetails && (
                <div className={classes.countLabel}>
                  <Typography variant="h5">Recipients count: </Typography>
                  <Typography variant="h5" className={classes.countData}>
                    {recipientDetailsCount || 0}
                  </Typography>
                </div>
              )}
              {eventIdentifierFields?.length > 0 ? (
                <TableComponent
                  isLoading={isLoadingDetails}
                  tableTitles={recipientDetailsColumns}
                  rowsCount={recipientDetailsCount}
                  filteredRows={recipientDetailsRows || []}
                  action={getRecipientDetails({ ...thisTemplate })}
                  disableTableSort={true}
                />
              ) : (
                <React.Fragment>
                  {isLoadingDetails ? (
                    <div className={classes.loader}>
                      <CircularProgress />
                    </div>
                  ) : (
                    <Typography className={classes.noResultsDiv}>
                      No support to view recipient details.
                    </Typography>
                  )}
                </React.Fragment>
              )}
            </CardContent>
          </form>
        </Card>
      )}
      {<SaveMessage openSnackbar={isTemplateSaved} />}
      {
        <PopupModal
          isOpen={showAlert}
          handleClose={handleClose}
          title="Error"
          severity="error"
          content="Error occured while fetching recipients count. Please try again."
          buttonGroup={getButtonGroup()}
        />
      }
    </div>
  );
};

export default Design;
